import { defineBoot } from '#q-app';
import { createI18n } from 'vue-i18n';
import type { I18n } from 'vue-i18n';
import { useLocalStorage, useNavigatorLanguage } from '@vueuse/core';
import messages from 'src/i18n';

export type MessageLanguages = keyof typeof messages;
export type MessageSchema = (typeof messages)['en-US'];

declare module 'vue-i18n' {
  export interface DefineLocaleMessage extends MessageSchema {
    [key: string]: MessageSchema;
  }
  export interface DefineDateTimeFormat {
    [key: string]: string;
  }
  export interface DefineNumberFormat {
    [key: string]: string;
  }
}

const localeStorage = useLocalStorage('locale', '');

// Map browser locales to your supported locales
const mapDeviceLocale = (deviceLocale: string): string => {
  console.log('Raw device locale:', deviceLocale);

  // Extract language code (en-US -> en, he-IL -> he, etc.)
  const langCode = deviceLocale?.split?.('-')?.[0]?.toLowerCase() || 'en';

  // Map to your supported locales based on CSV file
  const localeMap: Record<string, string> = {
    en: 'en-US',
    he: 'he-IL',
    uk: 'uk-UA',
    iw: 'he-IL', // iOS sometimes returns 'iw' for Hebrew
  };

  const mappedLocale = localeMap[langCode] || 'en-US';
  console.log('Mapped locale:', mappedLocale);
  return mappedLocale;
};

const getBrowserLanguage = (): string => {
  try {
    const navigatorLanguage = useNavigatorLanguage();
    const rawLocale = navigatorLanguage.language.value;
    return mapDeviceLocale(rawLocale ?? 'en-US');
  } catch (error) {
    console.error('Failed to get browser language:', error);
    return 'en-US';
  }
};

let i18n: I18n;
let t: (key: keyof MessageSchema, ...args: unknown[]) => string;

export default defineBoot(({ app }) => {
  console.log('Initializing i18n...');

  // getBrowserLanguage() already falls back to 'en-US' on failure.
  const detectedLocale = getBrowserLanguage();

  // Use stored locale or detected locale
  const finalLocale = localeStorage.value || detectedLocale;

  // Validate that the locale exists in messages
  const availableLocales = Object.keys(messages);
  const validLocale = availableLocales.includes(finalLocale)
    ? finalLocale
    : 'en-US';

  console.log('Available locales:', availableLocales);
  console.log('Using locale:', validLocale);

  i18n = createI18n({
    locale: validLocale,
    legacy: false,
    messages,
  });

  t = i18n.global.t;

  app.use(i18n);
});

// For use outside of components (script setup)
export { t, i18n };
